import { useState, useCallback, useRef } from 'react';
import { deriveSecretFromTimelock, secretToHashlock, bytesToHex, } from '@train-protocol/sdk';
import { useQueryClient } from '@tanstack/react-query';
import { useTrainContext } from '../providers/TrainContext';
import { useSwapActions } from '../internal/useSwapActions';
import { useSDStoreContext } from '../providers/SecretDerivationProvider';
import { useWalletContext } from '../wallet/WalletContext';
import { trainQueryKeys } from '../internal/queryKeys';
import { caip2Id, parseCaip2Id } from '../internal/branded';
import { TrainError, TrainErrorCode } from '../types';
import { useNetworksContext } from '../providers/NetworksProvider';
/**
 * Action hook to reveal the swap secret to the solver API.
 * Derives the secret on-demand from the internal key store + nonce
 * (from sourceDetails.userData in React Query cache).
 */
export function useRevealSecret() {
    const { apiClient, config } = useTrainContext();
    const actions = useSwapActions();
    const sdStore = useSDStoreContext();
    const walletCtx = useWalletContext();
    const queryClient = useQueryClient();
    const { networkMap } = useNetworksContext();
    const [isRevealing, setIsRevealing] = useState(false);
    const [error, setError] = useState(null);
    const inFlight = useRef(false);
    const reveal = useCallback(async (hashlock) => {
        if (inFlight.current)
            return;
        inFlight.current = true;
        setIsRevealing(true);
        setError(null);
        const reportError = (message) => {
            const err = new TrainError(message, TrainErrorCode.RevealFailed);
            setError(err);
            actions.updateSwapFlags(hashlock, { error: err });
            config.onError?.(err);
            inFlight.current = false;
            setIsRevealing(false);
            return err;
        };
        const swap = actions.getSwap(hashlock);
        if (!swap || !swap.hashlock) {
            throw reportError('Cannot reveal: missing hashlock');
        }
        // Derive secret on-demand from derivedKey + nonce
        const derivedKey = sdStore?.getState().derivedKey;
        if (!derivedKey) {
            throw reportError('Cannot reveal: not logged in (derivedKey unavailable)');
        }
        // Resolve nonce: try cache first, fall back to on-chain read
        let nonce = null;
        // Tier 1: React Query cache (fast path — works when polling is active)
        const sourceDetails = queryClient.getQueryData(trainQueryKeys.userLock(hashlock));
        const cachedNonce = sourceDetails?.userData ? Number(sourceDetails.userData) : null;
        if (cachedNonce && !isNaN(cachedNonce)) {
            nonce = cachedNonce;
        }
        // Tier 2: on-chain RPC fallback (cache was GC'd or not yet populated)
        if (!nonce && swap.source && swap.srcContract) {
            const sourceNetwork = caip2Id(swap.source);
            const sourceTokenDecimals = networkMap.get(swap.source)?.tokens.find(t => t.symbol == swap.source_asset)?.decimals;
            if (!sourceTokenDecimals) {
                throw reportError('Cannot reveal: source token decimals unavailable');
            }
            try {
                const client = walletCtx.createClient(sourceNetwork);
                const chainId = parseCaip2Id(sourceNetwork).reference;
                const details = await client.getUserLockDetails({
                    id: swap.hashlock,
                    chainId,
                    decimals: sourceTokenDecimals,
                    contractAddress: swap.srcContract,
                    txId: swap.txId ?? undefined,
                });
                const onChainNonce = details?.userData ? Number(details.userData) : null;
                if (onChainNonce && !isNaN(onChainNonce)) {
                    nonce = onChainNonce;
                }
            }
            catch {
                // RPC failure — fall through to the nonce-unavailable error below
            }
        }
        if (!nonce) {
            throw reportError('Cannot reveal: nonce unavailable from cache or on-chain data');
        }
        try {
            const secretBytes = deriveSecretFromTimelock(derivedKey, nonce);
            const secret = bytesToHex(Array.from(secretBytes));
            const normalize = (h) => (h.startsWith('0x') ? h : '0x' + h).toLowerCase();
            if (normalize(secretToHashlock(secret)) !== normalize(swap.hashlock)) {
                throw new TrainError('Cannot reveal: current login does not match the identity that created this swap', TrainErrorCode.RevealFailed);
            }
            await apiClient.revealSecret(swap.hashlock, secret, swap.destinationSolverAddress);
            actions.updateSwapFlags(hashlock, { secretRevealedToApi: true });
            actions.updateSwap(hashlock, { secretRevealed: true });
        }
        catch (err) {
            const trainError = new TrainError(err instanceof Error ? err.message : String(err), TrainErrorCode.RevealFailed, err);
            setError(trainError);
            actions.updateSwapFlags(hashlock, { error: trainError });
            config.onError?.(trainError);
            throw trainError;
        }
        finally {
            inFlight.current = false;
            setIsRevealing(false);
        }
    }, [apiClient, actions, sdStore, config, queryClient, walletCtx]);
    return { reveal, isRevealing, error };
}
